import { createColumnHelper } from "@tanstack/react-table";

import { QuantityWithUOMAbbr } from "@/components/QuantityWithUOMAbbr";
import { SOSEntityHashLink } from "@/components/SosEntityHashLink";
import { formatDineroCad } from "@/utils/dinero-util";
import { CostTaxBreakdown } from "../../queries/tracking-data/cost-tax-breakdown";
import { ExistingInventoryForItem } from "../../queries/tracking-data/existing-inventory";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";
import { PurchasingNoteTableRow, getPurchasingNoteColumnDef } from "../purchasing-progress/purchasing-note-column";
import { QuantityRequiredForJobCell } from "../QuantityRequiredForJobCell";
import { ExistingInventorySourceList } from "./ExistingInventorySourceList";

export interface ExistingInventoryTableRow extends ExistingInventoryForItem, PurchasingNoteTableRow {
  rowId: string;
  quantityRequiredForJob: number;
  fromInventoryCosts: CostTaxBreakdown;
  toInventoryCosts: CostTaxBreakdown;
}

// Create helpers for defining columns
const columnHelper = createColumnHelper<ExistingInventoryTableRow>();

const itemDetailColumns = [
  columnHelper.accessor("item.name", {
    header: "Item",
    enableHiding: false,
    cell: ({ row }) => (
      <JobTrackingInternalLink trackerPath="bom-by-component">
        {({ targetPath, targetPageTitle }) => (
          <SOSEntityHashLink
            fromHash={row.id}
            urlPath={targetPath}
            tooltipContent={`View on '${targetPageTitle}' page`}
            type="item"
            id={row.original.item.id}
            label={row.original.item.name}
          />
        )}
      </JobTrackingInternalLink>
    ),
  }),
  columnHelper.accessor("item.type", { header: "Type" }),
  columnHelper.accessor("item.description", { header: "Description" }),
];

const quantityColumns = [
  columnHelper.accessor("quantityRequiredForJob", {
    header: "Total Required",
    cell: ({ row }) => <QuantityRequiredForJobCell item={row.original.item} />,
    meta: { headerCellProps: { className: "border-start-double" }, cellProps: { className: "border-start-double" } },
  }),
  columnHelper.accessor("quantity.fromInventory", {
    cell: ({ row, getValue }) => <QuantityWithUOMAbbr quantity={getValue()} uom={row.original.item.baseItemUOM?.uom} />,
    header: "# From Inventory",
  }),
  columnHelper.accessor("quantity.toInventory", {
    cell: ({ row, getValue }) => <QuantityWithUOMAbbr quantity={getValue()} uom={row.original.item.baseItemUOM?.uom} />,
    header: "# To Inventory",
  }),
];

const valueColumns = [
  columnHelper.accessor("fromInventoryCosts.subtotal", {
    header: "From Inventory",
    cell: ({ getValue }) => formatDineroCad(getValue()),
    meta: { headerCellProps: { className: "border-start-double" }, cellProps: { className: "border-start-double" } },
  }),
  columnHelper.accessor("toInventoryCosts.subtotal", {
    header: "To Inventory",
    cell: ({ getValue }) => formatDineroCad(getValue()),
  }),
];

// Export the full set of columns for the table
// NOTE: We have to define the group columns separately before this because the type inference for the group columns is broken in Tanstack Table
// See: https://github.com/TanStack/table/issues/5065
export const existingInventoryTableColumns = [
  columnHelper.group({ header: "Item Details", columns: itemDetailColumns }),
  columnHelper.group({
    header: "Quantities",
    columns: quantityColumns,
    meta: { headerCellProps: { className: "border-start-double" } },
  }),
  columnHelper.group({
    header: "Values (Before Tax)",
    columns: valueColumns,
    meta: { headerCellProps: { className: "border-start-double" } },
  }),
  columnHelper.accessor("sources", {
    header: "Sources",
    cell: ({ row, getValue }) => (
      <ExistingInventorySourceList fromRowId={row.id} item={row.original.item} sources={getValue()} />
    ),
    meta: { headerCellProps: { className: "border-start-double" }, cellProps: { className: "border-start-double" } },
  }),
  getPurchasingNoteColumnDef<ExistingInventoryTableRow>({
    getItemId: (row) => row.item.id,
  }),
];
